import { useState, Children, cloneElement, Fragment, ReactNode, isValidElement } from 'react'
import cn from 'clsx'

// Components
import {
  Breadcrumbs,
  Button,
  Dropdown,
  IconClose,
  IconMoreInfo,
  IconPlaySmall,
  IconShareSmall,
  IconPen,
  Image,
  Link,
  Pill,
} from '@tracklib/kit'
import Modal from 'components/modal'
import Markdown from 'components/base/markdown'

// Hooks
import { useProfile } from 'modules/profile/hooks'

// Types
import { ApiRequest } from 'types'

// Utils
import { getAdminHost } from '@constants'
import { cleanseFromHtml } from 'utils'

import styles from './contentheader.css'

type DropdownLink = {
  name: string
  icon: ReactNode
  onClick?: () => void
  to?: string
  modal?: boolean
  query?: Record<string, unknown>
  href?: string
  separator?: boolean
}

type ContentHeaderProps = {
  playAll?: () => void
  playAllRequest?: ApiRequest
  type?: string
  shortType?: string
  title: string
  by?: ReactNode
  description?: string
  infoItems?: ReactNode
  image?: ReactNode
  adminEditPath?: string
  sharePath?: string
  children?: ReactNode
  breadcrumbs?: Array<{ link: string; text: string }>
  className?: string
  loading?: boolean
  dropdownLinks?: Array<DropdownLink>
  overflowVisible?: boolean
}

const ContentHeader = ({
  playAll,
  playAllRequest,
  type,
  shortType,
  title,
  by,
  description,
  infoItems,
  image,
  adminEditPath,
  sharePath,
  children,
  breadcrumbs,
  className,
  loading,
  dropdownLinks = [],
  overflowVisible,
}: ContentHeaderProps): JSX.Element => {
  const [dropdownOpen, toggleDropdownOpen] = useState(false)
  const [modalOpen, toggleModalOpen] = useState(false)
  const profile = useProfile()

  const ContentImage = () => (
    <div className={cn(styles.image, overflowVisible && styles.overflowVisible)}>
      {image ? (
        <>{typeof image === 'string' ? <Image image={image} alt={title} absolute sizes="200px" /> : image}</>
      ) : (
        <div className={styles.loadingImage} />
      )}
    </div>
  )

  const ContentInfo = ({ isModal }: { isModal?: boolean }) => {
    const cleansedDescription = description ? cleanseFromHtml(description).replace(/(\r\n|\n|\r)/gm, '') : null
    return (
      <>
        {type && title && (
          <Pill size="small" className={styles.pill}>
            {type}
          </Pill>
        )}
        {loading ? (
          <div className={styles.loadingTitle} />
        ) : (
          <h1 className={styles.title} datatesting="content_title">
            {title}
          </h1>
        )}
        {by && (
          <p className={styles.by}>
            <span className={styles.byText}>by</span>
            {by}
          </p>
        )}
        {loading ? (
          <div className={styles.loadingDescription} />
        ) : (
          <>
            {cleansedDescription && (
              <>
                {isModal ? (
                  <p className={styles.modalDescription}>{cleansedDescription}</p>
                ) : (
                  <div onClick={() => toggleModalOpen(!modalOpen)} role="button" tabIndex={0} className={styles.descriptionClickWrapper}>
                    <Markdown className={styles.description}>{cleansedDescription}</Markdown>
                  </div>
                )}
              </>
            )}
          </>
        )}
      </>
    )
  }

  const InfoItems = ({ isModal = false }: { isModal?: boolean }) => (
    <div className={cn(styles.infoItems, Children.count(infoItems) > 3 && styles.infoItemsGrid)}>
      {Children.map(infoItems, (x, i) => (
        <Fragment key={String(i)}>
          {isValidElement(x) &&
            cloneElement(x, {
              isModal,
              modalOpen,
              toggleModalOpen,
            })}
        </Fragment>
      ))}
    </div>
  )

  const dropdownItems: Array<DropdownLink> = [
    {
      name: `View ${shortType || type} info`,
      icon: <IconMoreInfo />,
      onClick: () => toggleModalOpen(true),
    },
    ...(sharePath
      ? [
          {
            name: `Share ${shortType || type}`,
            icon: <IconShareSmall />,
            to: 'share',
            modal: true,
            query: { url: sharePath },
          },
        ]
      : []),
    ...dropdownLinks,
    ...(profile.is_staff && adminEditPath
      ? [{ name: 'Edit (admin)', icon: <IconPen />, href: `${getAdminHost()}/admin${adminEditPath}`, separator: true }]
      : []),
  ]

  return (
    <>
      <Modal modalSize="XXL" isOpen={modalOpen} mobileFullscreen onClose={() => toggleModalOpen(false)}>
        <div className={styles.modalWrapper}>
          <aside className={styles.modalLeftSide}>
            <ContentImage />
          </aside>
          <div>
            <button className={styles.closeButton} onClick={() => toggleModalOpen(false)}>
              <IconClose />
            </button>
            <ContentInfo isModal />
            <InfoItems isModal />
          </div>
        </div>
      </Modal>
      <div className={cn(styles.wrapper, className)} datatesting="contentheader">
        {loading ? (
          <div className={styles.loadingBreadcrumbs} />
        ) : (
          <>{breadcrumbs && <Breadcrumbs items={breadcrumbs} className={styles.breadcrumbs} />}</>
        )}
        <div className={styles.innerWrapper}>
          <ContentImage />
          <div className={styles.main}>
            <div className={styles.trackInfo}>
              <div className={styles.contentInfoWrapper}>
                <ContentInfo />
              </div>
              {infoItems ? (
                <div className={styles.infoItemsWrapper}>
                  <InfoItems />
                </div>
              ) : null}
              {children}
            </div>
            <div>
              <Dropdown
                active={dropdownOpen}
                className={styles.dropdown}
                label={<span className={styles.dots} />}
                labelClassName={styles.labelButton}
                listClassName={styles.list}
                closesOnBackdropClick
                onToggle={() => toggleDropdownOpen(!dropdownOpen)}
                datatesting="contentheader_dropdown"
              >
                {dropdownItems.map(({ name, onClick, icon, to, modal, query, href, separator }) => (
                  <Dropdown.Item key={name} className={cn(styles.item, separator && styles.separator)}>
                    <Link
                      to={to}
                      href={href}
                      target={href ? '_blank' : null}
                      modal={modal}
                      query={query}
                      className={styles.itemLink}
                      onClick={() => {
                        if (onClick) {
                          onClick()
                        }
                        return toggleDropdownOpen(false)
                      }}
                    >
                      <span className={styles.itemIcon}>{icon}</span>
                      <span className={styles.itemText}>{name}</span>
                    </Link>
                  </Dropdown.Item>
                ))}
              </Dropdown>
              {playAll && playAllRequest && (
                <Button
                  className={styles.playButton}
                  onClick={playAll}
                  isLoading={playAllRequest.isLoading}
                  disabled={playAllRequest.isLoading}
                >
                  <>
                    <IconPlaySmall /> Play {type && type.toLowerCase()}
                  </>
                </Button>
              )}
            </div>
          </div>
        </div>
      </div>
    </>
  )
}

export default ContentHeader
